'use client';

import React, { useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import CTACard from '@/components/common/CTACard';
import { marked } from 'marked';
import type { ContentPage } from '@/lib/supabase';
import { extractFaqs, buildFaqJsonLd } from '@/lib/extractFaqs';
import { buildHreflangLinks } from '@/lib/hreflang';

interface LandingLayoutProps {
  page: ContentPage;
  localeVariants?: { slug: string }[];
}

function addHeadingIds(html: string): string {
  return html.replace(
    /<(h[23])>(.*?)<\/\1>/gi,
    (_, tag, text) => {
      const id = text
        .replace(/<[^>]+>/g, '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      return `<${tag} id="${id}">${text}</${tag}>`;
    }
  );
}

const LandingLayout: React.FC<LandingLayoutProps> = ({ page, localeVariants = [] }) => {
  const contentHtml = useMemo(
    () => addHeadingIds(marked.parse(page.body_markdown) as string),
    [page.body_markdown]
  );

  const isSolutionsPage = page.slug.includes('solutions/') || page.slug.includes('features/');

  const jsonLd = useMemo(() => {
    const base: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': page.schema_type || 'WebPage',
      name: page.title,
      description: page.description,
      url: `https://intelgrader.com/${page.slug}`,
      publisher: { '@type': 'Organization', name: 'IntelGrader', url: 'https://intelgrader.com', logo: { '@type': 'ImageObject', url: 'https://intelgrader.com/assets/logo-wordmark.png' } },
      dateModified: page.updated_at,
    };
    if (isSolutionsPage) {
      base.speakable = {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', '.prose-content h2', '.prose-content p:first-of-type'],
      };
    }
    return base;
  }, [page, isSolutionsPage]);

  const breadcrumbLd = useMemo(() => {
    const parts = page.slug.split('/');
    const items = [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://intelgrader.com/' },
    ];
    if (parts.length > 1) {
      items.push({
        '@type': 'ListItem',
        position: 2,
        name: page.title,
        item: `https://intelgrader.com/${page.slug}`,
      });
    }
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items,
    };
  }, [page]);

  const faqJsonLd = useMemo(() => {
    const faqs = extractFaqs(page.body_markdown);
    return buildFaqJsonLd(faqs);
  }, [page.body_markdown]);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      {faqJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />}

      <div className="max-w-4xl mx-auto px-6 py-16 md:py-24">
        {/* Breadcrumb */}
        <nav className="text-sm text-[var(--color-muted)] mb-8">
          <Link href="/" className="hover:text-[var(--color-primary)]">Home</Link>
          <span className="mx-2">/</span>
          <span>{page.title}</span>
        </nav>

        {/* Hero */}
        <header className="mb-12">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--color-text)] tracking-tight leading-tight mb-6">
            {page.title}
          </h1>
          {page.description && (
            <p className="text-lg md:text-xl text-[var(--color-muted)] max-w-2xl">
              {page.description}
            </p>
          )}
        </header>

        {page.image_url && (
          <Image
            src={page.image_url}
            alt={page.image_alt || page.title}
            className="w-full rounded-xl mb-12"
            width={1200}
            height={630}
            priority
          />
        )}

        {/* Body content */}
        <div
          className="prose-content"
          dangerouslySetInnerHTML={{ __html: contentHtml }}
        />

        {/* Bottom CTA */}
        <CTACard variant="primary" />
      </div>
    </>
  );
};

export default LandingLayout;
